// Copyright (C) 2026 FlexiProof. All rights reserved.
//
// This source code file is a part of free software licensed under the terms of
// the MIT License as published by the Massachusetts Institute of Technology:
// you can use, copy, modify and distribute any part of it without limitation,
// subject to the conditions contained within that license.
//
// This source code file, and the software it forms a part of, IS PROVIDED "AS
// IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. See the MIT License
// for more details.
//
// You should have received a copy of the MIT License along with this source
// code file in the root of this repository. If not, see one of the following
// 3rd party sites for a copy of the license template:
// - <https://opensource.org/licenses/MIT>
// - <https://choosealicense.com/licenses/mit>
// - <https://spdx.org/licenses/MIT>

/**
 * @file Product class built only with common properties available from server.
 * @author James Reid
 */

// @ts-check

// @@imports-types
import {
    ProductData,
    Unit,
    UnitType,
    ProductDisplayCategory,
    UNITS,
    UNIT_TYPES,
    PRODUCT_DISPLAY_CATEGORIES
} from "../types/index.js"

// @@body
/**
 * Internal base product class reflecting data extracted from the database on
 * product details, current price, and stock. May be extended to include
 * additional data inferred from specific sku code formats or product names.
 */
class Product {
    /**
     * Fetches all data from the product data and sets each property as class
     * properties. Implements defaults for optional values.
     * @param {ProductData} data Product data passed from calculator plugin
     * implementation script. Product data originates from the database/server
     * depending on plugin context (for example WordPress and WooCommerce).
     * @param {object} obj Optional destructured parameters object.
     * @param {number} [obj.minimumOrder] Minimum order for product.
     * @param {number} [obj.minimumFraction] Minimum fraction of a single unit
     * which can be ordered.
     * @param {number} [obj.stockLimit] Optional stock limit which calculator
     * should not sell below.
     * @param {number} [obj.quantity] Quantity of product provided in a single
     * order (eg. 1 single unit, 0.5L of product, 10kg of product etc.).
     * @param {Unit} [obj.unit] Optional override for the *unit* of a single
     * order of this product (eg. 1 single unit, 5 pcs, 10kg etc.).
     * @param {boolean} [obj.isUniqueLineItem] Should product be a unique line
     * item in a bom instance (i.e. if the product is added multiple times,
     * should the quantity of the original line item be updated, or should a new
     * line item be added to the bom instance)?
     */
    constructor(data, {
        minimumOrder = 1,
        minimumFraction = 1,
        stockLimit = 0,
        quantity = 1,
        unit,
        isUniqueLineItem = true
    } = {}) {
        // Set product properties directly from data returned by server,
        // providing defaults etc. where required.
        this.uuid = data.uuid
        this.sku = data.skus.merchant || data.skus.product
        this.code = data.skus.calculator.match(/(?<=^_[0-9A-F]{4}_).*/)?.[0] || ""
        this.displayOrder = parseInt(
            data.skus.calculator.match(/(?<=^_)[0-9A-F]{4}(?=_)/)?.[0] || "0",
            16
        )
        this.name = data.name
        this.tableDetail = ["Name", this.name]
        this.description = data.description
        this.stock = data.stock === null ? Infinity : data.stock
        this.stockLimit = stockLimit || 0
        this.runningStock = this.stock - this.stockLimit
        this.unit = unit || UNITS.custom.single
        this.quantity = quantity
        this.price = data.price
        this.currency = data.currency
        this.productUrl = data.urls.product
        this.imageUrl = data.urls.image
        this.installationUrl = data.urls.installation
        this.isUniqueLineItem = isUniqueLineItem
        this.minimumOrder = minimumOrder
        this.minimumFraction = minimumFraction

        // Type string unset as type property has a different meaning for each
        // product. Category string set to wildcard and should be set in
        // extended class. Note that the category is a *display string* for
        // rendering in bom.
        this.type = /** @type {string?} */ ("")
        this.displayCategory = /** @type {ProductDisplayCategory} */
            (PRODUCT_DISPLAY_CATEGORIES.other)

        // Set SI product dimensions from values provided by server according to
        // expected unit from server. Force defined weight for calculations of
        // total material weight of order/consignment.
        this.width = Product.#getSiDimension(
            data.dimensions.width || NaN,
            UNITS.length.metre,
            UNIT_TYPES.length
        )
        this.height = Product.#getSiDimension(
            data.dimensions.height || NaN,
            UNITS.length.metre,
            UNIT_TYPES.length
        )
        this.depth = Product.#getSiDimension(
            data.dimensions.depth || NaN,
            UNITS.length.metre,
            UNIT_TYPES.length
        )
        this.length = Product.#getSiDimension(
            data.dimensions.length || NaN,
            UNITS.length.metre,
            UNIT_TYPES.length
        )
        this.thickness = Product.#getSiDimension(
            data.dimensions.thickness || NaN,
            UNITS.length.millimetre,
            UNIT_TYPES.length
        )
        this.volume = Product.#getSiDimension(
            data.dimensions.volume || NaN,
            UNITS.volume.litre,
            UNIT_TYPES.volume
        )
        this.weight = Product.#getSiDimension(
            data.dimensions.weight || NaN,
            UNITS.mass.kilogram,
            UNIT_TYPES.mass,
            { forceFinite: true }
        )
    }

    /**
     * Reset the running stock quantity of the current product instance for a
     * roof materials calculation.
     */
    resetRunningStockQuantity() {
        this.runningStock = this.stock - this.stockLimit
    }

    /**
     * Get SI unit value of a given dimension.
     * @param {number} value Dimension value to convert into SI units.
     * @param {Unit} unit Source unit.
     * @param {UnitType} unitType Source unit type/dimension.
     * @param {object} obj Optional destructured parameters object.
     * @param {number} [obj.factor] Custom factor which will be used for
     * conversion if set.
     * @param {boolean} [obj.forceFinite] Should result value be forced to a
     * finite, no-NaN value?
     * @param {number} [obj.defaultValue] Default value to be used if force
     * finite flag is set and value is not finite.
     * @returns {number} Input value converted to SI units.
     */
    static #getSiDimension(value, unit, unitType, {
        factor,
        forceFinite = false,
        defaultValue = 0
    } = {}) {
        // Set conversion factor.
        // @ts-ignore - Keys used *will* correctly index conversions object.
        factor ??= /** @type {number} */ (Product
            .#conversionsByUnitType[unitType][unit])

        // Convert value to SI and force a finite, non-NaN value if required.
        value *= factor
        value = forceFinite && isNaN(value) ? defaultValue : value

        return value
    }

    /**
     * Unit type keyed object of dimension unit conversions to SI units.
     */
    static get #conversionsByUnitType() {
        return {
            length: {
                metre: 1,
                millimetre: 0.001,
                inch: 0.0254
            },
            volume: {
                litre: 1,
                millilitre: 0.001
            },
            mass: {
                kilogram: 1
            }
        }
    }
}

// @@exports
export { Product }
